import type { DispatchRecord } from "../api";
import { dailySeries } from "../lib/analytics";
import { formatCount } from "../lib/metrics";
import { MiniChart } from "./MiniChart";

/**
 * A compact dashboard bubble that sits beside the Next Post card: one headline
 * performance metric over the last 30 days, a sparkline, its 7-day trend, and a
 * jump to the full Analytics page. Picks the richest metric the account actually
 * has data for (engagement → views → posts) so it's never an empty "—".
 */
export function AnalyticsBubble({
  history,
  onViewAnalytics,
}: {
  history: DispatchRecord[];
  onViewAnalytics: () => void;
}) {
  const series = dailySeries(history, 30);
  const sum = (a: number[]) => a.reduce((x, y) => x + y, 0);
  const engagementTotal = sum(series.engagement);
  const viewsTotal = sum(series.views);

  // Choose the most meaningful available metric.
  const metric =
    engagementTotal > 0
      ? { label: "Engagement", points: series.engagement }
      : viewsTotal > 0
        ? { label: "Views", points: series.views }
        : { label: "Posts published", points: series.posts };

  const total = sum(metric.points);
  const last7 = metric.points.slice(-7).reduce((a, b) => a + b, 0);
  const prev7 = metric.points.slice(-14, -7).reduce((a, b) => a + b, 0);
  const delta = last7 - prev7;

  return (
    <div className="analytics-bubble">
      <div className="analytics-bubble-head">
        <span className="analytics-bubble-eyebrow">{metric.label} · 30 days</span>
      </div>
      <div className="analytics-bubble-value">{formatCount(total)}</div>
      <MiniChart points={metric.points} />
      <div className="analytics-bubble-foot">
        {prev7 > 0 || last7 > 0 ? (
          <span className={`ops-delta ${delta > 0 ? "up" : delta < 0 ? "down" : ""}`}>
            {delta > 0 ? "▲" : delta < 0 ? "▼" : "•"} {formatCount(Math.abs(delta))} vs prev 7d
          </span>
        ) : (
          <span className="muted small">No trend yet</span>
        )}
        <button type="button" className="link-btn" onClick={onViewAnalytics}>
          View analytics →
        </button>
      </div>
    </div>
  );
}
